import { Injectable } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { ActiveCartFacade, MultiCartFacade } from '@spartacus/cart/base/root';
import { OCC_CART_ID_CURRENT, OCC_USER_ID_ANONYMOUS, UserIdService, WindowRef } from '@spartacus/core';
import { BehaviorSubject, Subscription, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, take, tap } from 'rxjs/operators';
import { GenMultiCartService } from '../features/cart/core/facade/multi-cart.service';
import { GenAutoShipCartService } from './auto-ship-cart.service';

@Injectable({ providedIn: 'root' })
export class GenAutoShipActiveCartService {
  static readonly CART_URL = '/cart?autoShip=true';
  private static readonly PREVIOUS_CART_ID_KEY = 'generac.previousCartIdBeforeAutoShip';

  private readonly autoShipRouteSubject = new BehaviorSubject<boolean>(false);
  readonly isAutoShipRoute$ = this.autoShipRouteSubject.asObservable();
  private readonly autoShipPageSubject = new BehaviorSubject<boolean>(false);
  readonly isAutoShipPage$ = this.autoShipPageSubject.asObservable();

  private subscription?: Subscription;

  constructor(
    private router: Router,
    private activeCartFacade: ActiveCartFacade,
    private multiCartFacade: MultiCartFacade,
    private multiCartService: GenMultiCartService,
    private autoShipCartService: GenAutoShipCartService,
    private userIdService: UserIdService,
    private windowRef: WindowRef,
  ) { }

  initialize(): void {
    if (this.subscription) {
      return;
    }

    const routeContext$ = this.router.events.pipe(
      filter((event): event is NavigationStart | NavigationEnd =>
        event instanceof NavigationStart || event instanceof NavigationEnd
      ),
      map((event) => event instanceof NavigationEnd ? event.urlAfterRedirects : event.url),
      // Blocking initial navigation can start before this initializer subscribes.
      startWith(this.windowRef.location
        ? this.windowRef.location.pathname + this.windowRef.location.search
        : this.router.url),
      map((url) => this.getRouteContext(url)),
      distinctUntilChanged(),
      tap((routeContext) => {
        this.autoShipRouteSubject.next(routeContext !== 'regular');
        this.autoShipPageSubject.next(routeContext === 'products' || routeContext === 'autoShip');
      }),
    );

    this.subscription = combineLatest([
      routeContext$,
      this.userIdService.getUserId().pipe(distinctUntilChanged()),
    ]).subscribe(([routeContext, userId]) => {
      if (!userId || userId === OCC_USER_ID_ANONYMOUS) {
        return;
      }
      if (routeContext !== 'regular') {
        this.activateAutoShipCart(routeContext !== 'products');
      } else {
        this.restorePreviousCart();
      }
    });
  }

  private activateAutoShipCart(loadCart: boolean): void {
    combineLatest([
      this.activeCartFacade.getActiveCartId().pipe(take(1)),
      this.userIdService.getUserId().pipe(take(1)),
    ]).pipe(take(1)).subscribe(([activeCartId, userId]) => {
      if (!userId || userId === OCC_USER_ID_ANONYMOUS) {
        return;
      }

      if (!this.getPreviousCartId()) {
        this.setPreviousCartId(activeCartId || OCC_CART_ID_CURRENT);
      }

      this.multiCartService.setActiveCartId(GenAutoShipCartService.CART_ID);
      if (loadCart) {
        this.autoShipCartService.load(userId).pipe(take(1)).subscribe({
          error: () => undefined,
        });
      }
    });
  }

  private restorePreviousCart(): void {
    const previousCartId = this.getPreviousCartId();
    if (!previousCartId) {
      return;
    }

    this.userIdService.getUserId().pipe(take(1)).subscribe((userId) => {
      if (!userId || userId === OCC_USER_ID_ANONYMOUS) {
        return;
      }

      this.multiCartService.setActiveCartId(previousCartId);
      this.multiCartFacade.loadCart({
        userId,
        cartId: previousCartId || OCC_CART_ID_CURRENT,
        extraData: { active: true },
      });
      this.clearPreviousCartId();
    });
  }

  private getRouteContext(url: string): 'regular' | 'products' | 'autoShip' | 'cart' {
    const tree = this.router.parseUrl(url);
    const path = '/' + (tree.root.children['primary']?.segments.map(segment => segment.path).join('/') ?? '');
    if (path === '/cart' && tree.queryParams['autoShip'] === 'true') {
      return 'cart';
    }
    if (path === '/auto-ship/products') {
      return 'products';
    }
    return path === '/auto-ship' || path.startsWith('/auto-ship/')
      ? 'autoShip'
      : 'regular';
  }

  private getPreviousCartId(): string | null {
    return this.windowRef.sessionStorage?.getItem(
      GenAutoShipActiveCartService.PREVIOUS_CART_ID_KEY,
    ) ?? null;
  }

  private setPreviousCartId(cartId: string): void {
    this.windowRef.sessionStorage?.setItem(
      GenAutoShipActiveCartService.PREVIOUS_CART_ID_KEY,
      cartId,
    );
  }

  private clearPreviousCartId(): void {
    this.windowRef.sessionStorage?.removeItem(
      GenAutoShipActiveCartService.PREVIOUS_CART_ID_KEY,
    );
  }
}
